import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router'
import type { Command, CommandAction } from '@/entities/command'
import { cx, formatMoney, formatNumber, formatTime } from '@/shared/lib'
import { Button } from '@/shared/ui/Button'
import { Icon } from '@/shared/ui/Icon'
import { Link } from '@/shared/ui/Link'
import { ProgressBar } from '@/shared/ui/ProgressBar'
import { StatusBadge } from '@/shared/ui/StatusBadge'
import { STATUS_TONE, TYPE_ICON, isKnownError, resultUrl, subjectPath } from '../lib/commandView'
import s from './CommandCard.module.css'

// CSS-модуль типизирован индексной сигнатурой, поэтому значения — string | undefined
const TONE_CLASS: Record<Command['status'], string | undefined> = {
  accepted: undefined,
  sent: undefined,
  done: s.done,
  failed: s.failed,
  expired: s.expired,
}

const GLYPH_CLASS: Record<Command['status'], string | undefined> = {
  accepted: undefined,
  sent: s.glyphSent,
  done: s.glyphDone,
  failed: s.glyphFailed,
  expired: s.glyphExpired,
}

type CommandCardProps = {
  command: Command
  /** Какое действие сейчас выполняется по этой карточке: кнопка ждёт ответа. */
  pendingAction: CommandAction | null
  onAction: (action: CommandAction) => void
}

/** Секунды → «≈ 20 сек» / «≈ 2 мин»: меньше минуты — секунды, дальше минуты вверх. */
function useEta() {
  const { t } = useTranslation('processes')
  return (seconds: number) =>
    seconds < 60
      ? t('card.etaSeconds', { count: seconds })
      : t('card.etaMinutes', { count: Math.ceil(seconds / 60) })
}

/**
 * Одна команда в мониторе. Пять состояний контракта — пять раскладок:
 * - `sent` — шкала хода, кто запустил и когда, оценка времени, «Отменить»;
 * - `accepted` — «в очереди», автоповтор и «Проверить сейчас»;
 * - `failed` — красная карточка: код отказа текстом, шаг обрыва, время, «Повторить»;
 * - `expired` — жёлтая: 1С не ответила до срока, «Повторить»;
 * - `done` — зелёная: время и итог по типу, ссылка на файл, если он есть.
 * Действия рисуются только из `actions` — что можно, решает бэк (правило 11).
 */
export function CommandCard({ command, pendingAction, onAction }: CommandCardProps) {
  const { t } = useTranslation('processes')
  const eta = useEta()
  const { status, subject, steps } = command
  const path = subjectPath(command)
  const file = resultUrl(command)

  const title =
    steps !== null && status !== 'failed' && command.type === 'reserve.place'
      ? t('card.titleWithPositions', {
          type: t(`type.${command.type}`),
          positions: t('card.positions', { count: steps.total }),
        })
      : t(`type.${command.type}`)

  const subjectParts = [
    subject.number,
    subject.detail,
    subject.amount !== null ? formatMoney(subject.amount, subject.currency ?? undefined) : null,
  ].filter((part): part is string => part !== null && part !== '')

  // Процент — только у идущей со шкалой; очередь называет капсула статуса
  const percent =
    command.progress !== null && status === 'sent'
      ? t('card.percent', { value: command.progress })
      : null

  const errorText = t(
    isKnownError(command.error) ? `card.error.${command.error}` : 'card.error.unknown',
  )

  const meta = (() => {
    switch (status) {
      case 'sent': {
        const started =
          command.startedBy !== null
            ? t('card.startedBy', { name: command.startedBy, time: formatTime(command.createdAt) })
            : t('card.startedAt', { time: formatTime(command.createdAt) })
        const parts = [
          steps !== null ? t('card.steps', { done: steps.done, total: steps.total }) : started,
          command.etaSeconds !== null ? eta(command.etaSeconds) : null,
        ]
        return parts.filter((part): part is string => part !== null)
      }
      case 'accepted':
        return command.retry !== null
          ? [
              t('card.retry', {
                minutes: Math.max(1, Math.round(command.retry.everySeconds / 60)),
                time: formatTime(command.retry.nextAt),
              }),
            ]
          : [t('card.waiting')]
      case 'failed':
        return [
          errorText,
          steps !== null ? t('card.failedStep', { done: steps.done, total: steps.total }) : null,
          formatTime(command.updatedAt),
        ].filter((part): part is string => part !== null)
      case 'expired':
        return [t('card.expiredAt', { time: formatTime(command.expiresAt) })]
      case 'done':
        return [
          t('card.doneAt', { time: formatTime(command.updatedAt) }),
          resultText(command),
        ].filter((part): part is string => part !== null)
    }
  })()

  function resultText(done: Command): string | null {
    const result = done.result
    switch (done.type) {
      case 'price.sync':
        return result
          ? t('card.result.price.sync', {
              positions: formatNumber(Number(result.positions ?? 0)),
              priceChanges: formatNumber(Number(result.priceChanges ?? 0)),
            })
          : null
      case 'invoice.generate':
        return result?.number !== undefined
          ? t('card.result.invoice.generate', { number: result.number })
          : null
      case 'reserve.place':
        return result
          ? t('card.result.reserve.place', {
              reserved: result.reserved ?? 0,
              total: result.total ?? 0,
            })
          : null
      case 'documents.export':
        return result
          ? t('card.result.documents.export', { count: Number(result.count ?? 0) })
          : null
      case 'report.build':
        return t('card.result.report.build')
      case 'payment.check':
        return result?.status === 'paid'
          ? t('card.result.payment.paid')
          : t('card.result.payment.unpaid')
      case 'edo.upload':
        return t('card.result.edo.upload')
      case 'write-off':
        return t('card.result.write-off')
    }
  }

  const toneClass = TONE_CLASS[status]

  return (
    <li className={cx(s.root, toneClass)} data-status={status}>
      <span className={cx(s.glyph, GLYPH_CLASS[status])} aria-hidden="true">
        <Icon name={TYPE_ICON[command.type]} size="sm" />
      </span>

      <div className={s.body}>
        <div className={s.head}>
          <span className={s.title}>{title}</span>
          <span className={s.aside}>
            {percent !== null ? <span className={s.percent}>{percent}</span> : null}
            <StatusBadge
              tone={STATUS_TONE[status]}
              size="xs"
              indicator={status === 'sent' ? 'spinner' : 'dot'}
            >
              {t(`status.${status}`)}
            </StatusBadge>
          </span>
        </div>

        {subjectParts.length > 0 ? (
          <span className={s.subject}>
            {path !== null ? <RouterLink to={path}>{subjectParts[0]}</RouterLink> : subjectParts[0]}
            {subjectParts.slice(1).map((part) => (
              <span key={part}>
                {' · '}
                {part}
              </span>
            ))}
          </span>
        ) : null}

        {status === 'sent' || status === 'accepted' ? (
          <ProgressBar
            value={status === 'sent' ? (command.progress ?? 0) : 0}
            max={100}
            size="xs"
            tone="accent"
            decorative
            className={cx(s.bar, status === 'accepted' && s.barQueued)}
          />
        ) : null}

        <div className={s.foot}>
          <span className={s.meta}>
            {meta.map((part, index) => (
              <span key={part}>
                {index > 0 ? ' · ' : null}
                {part}
              </span>
            ))}
          </span>

          <span className={s.actions}>
            {file !== null ? (
              <Link
                variant="inline"
                size="sm"
                href={file}
                external
                externalLabel={t('card.action.openExternal')}
              >
                {t('card.action.download')}
              </Link>
            ) : null}
            {command.actions.map((action) => (
              <Button
                key={action}
                size="sm"
                variant={action === 'cancel' ? 'ghost' : 'tonal'}
                tone={action === 'cancel' ? 'danger' : 'accent'}
                loading={pendingAction === action}
                loadingLabel={t(`card.action.${action}Pending`)}
                disabled={pendingAction !== null && pendingAction !== action}
                onClick={() => onAction(action)}
              >
                {t(`card.action.${action}`)}
              </Button>
            ))}
          </span>
        </div>
      </div>
    </li>
  )
}
